import React from 'react';
import { flowRight } from 'lodash';
import {
  type LayoutName,
  getPostCover,
  LAYOUT_NAME_SIDE_BY_SIDE_MOBILE,
  getOuterUrl,
} from '@wix/communities-blog-client-common';
import VideoEmbed from '../../../post-page/components/video-embed';
import withDeviceType from '../../hoc/with-device-type';
import withSectionUrl, { type WithSectionUrl } from '../../hoc/with-section-url';
import { getImageContainerSize } from '../../services/get-post-cover-height';
import { type NormalizedPost } from '../../types';
import Link from '../link/internal-link';
import PostListItemImage from '../post-list-item-image';
import { PostListItemVideoThumbnail } from '../post-list-item-video-thumbnail';

type PostCover = ReturnType<typeof getPostCover>;

type CoverPost = Parameters<typeof getPostCover>[0] &
  Pick<NormalizedPost, 'title' | 'link'> & {
    media?: { embedMedia?: EmbedMedia; altText?: string };
  };

type EmbedMedia = {
  thumbnail?: { url?: string; height?: number; width?: number };
  video?: { url?: string };
};

type Oembed = PostCover['videoMetadata'];

type SharedCoverProps = {
  post: CoverPost;
  postLink: string;
  type: LayoutName;
  canPlayVideo?: boolean;
  videoClassName?: string;
  imageClassName?: string;
  isPublic?: boolean;
  isMobile: boolean;
  useAlternateDimesions?: boolean;
  width?: number;
  height?: number;
};

type Props = SharedCoverProps &
  WithSectionUrl & {
    withWrapper?: boolean;
    wrapperClassName?: string;
    withoutContainerStyle?: boolean;
  };

const renderVideoEmbed = ({
  oembed,
  postLink,
  canPlayVideo,
  videoClassName,
  height = 140,
  embedMedia,
  post,
}: Pick<SharedCoverProps, 'postLink' | 'canPlayVideo' | 'videoClassName' | 'height' | 'post'> & {
  oembed?: Oembed;
  embedMedia?: EmbedMedia;
}) => {
  if (!oembed && !embedMedia) {
    return null;
  }

  let video = oembed;

  if (!video && embedMedia) {
    video = {
      thumbnail_url: embedMedia.thumbnail?.url,
      video_url: embedMedia.video?.url,
      thumbnail_height: embedMedia.thumbnail?.height,
      thumbnail_width: embedMedia.thumbnail?.width,
    };
  }

  const style = { flexBasis: `${height}px` };

  return canPlayVideo ? (
    <VideoEmbed
      alt={post?.media?.altText ?? post?.title}
      oembed={video}
      className={videoClassName}
      style={style}
    />
  ) : (
    <div className={videoClassName} style={style}>
      <Link tabIndex={-1} to={postLink}>
        <PostListItemVideoThumbnail oembed={video} altText="" />
      </Link>
    </div>
  );
};

const renderImage = ({
  image,
  url,
  isPublic,
  postLink,
  type,
  imageClassName,
  isMobile,
  useAlternateDimesions,
  width,
  height,
  oembed,
  withoutContainerStyle,
  post,
}: Omit<SharedCoverProps, 'canPlayVideo' | 'videoClassName'> & {
  image?: PostCover['imageMetadata'];
  url?: string;
  oembed?: Oembed;
  withoutContainerStyle?: boolean;
}) => {
  const sideBySideMobileLayout = type === LAYOUT_NAME_SIDE_BY_SIDE_MOBILE;

  let preparedImageSrc;

  if (image) {
    preparedImageSrc = image;
  } else if (sideBySideMobileLayout && oembed && oembed.thumbnail_url) {
    preparedImageSrc = oembed.thumbnail_url;
  } else {
    return null;
  }

  const imgContainerSize = getImageContainerSize(image, type, useAlternateDimesions);
  const containerStyle =
    isMobile || withoutContainerStyle
      ? {}
      : {
          height: `${height || imgContainerSize.height}px`,
          minHeight: `${height || imgContainerSize.height}px`,
        };

  return (
    <div style={containerStyle} className={imageClassName}>
      <Link
        tabIndex={-1}
        fullRoute={post.link}
        to={postLink}
        aria-hidden={isMobile ? 'true' : undefined}
        ariaLabel={post?.media?.altText ?? post.title}
      >
        <PostListItemImage
          image={preparedImageSrc}
          url={post.link || url}
          isPublic={isPublic}
          // renderImage bails out above without an image, and the container size
          // is derived from it, so both dimensions are resolved by this point.
          height={(height || imgContainerSize.height)!}
          width={(width || imgContainerSize.width)!}
          layoutName={type}
        />
      </Link>
    </div>
  );
};
const PostListItemCover = ({
  post,
  postLink,
  type,
  canPlayVideo,
  videoClassName,
  imageClassName,
  isPublic,
  isMobile,
  useAlternateDimesions,
  width,
  height,
  sectionUrl,
  withWrapper = false,
  wrapperClassName,
  withoutContainerStyle = false,
}: Props) => {
  const { shouldRender, imageMetadata, videoMetadata } = getPostCover(post);

  if (!shouldRender) {
    return null;
  }

  const cover =
    renderImage({
      image: imageMetadata,
      url: getOuterUrl(postLink, sectionUrl),
      isPublic,
      postLink,
      type,
      imageClassName,
      isMobile,
      useAlternateDimesions,
      width,
      height,
      oembed: videoMetadata,
      withoutContainerStyle,
      post,
    }) ||
    renderVideoEmbed({
      oembed: videoMetadata,
      postLink,
      canPlayVideo,
      videoClassName,
      height,
      embedMedia: post?.media?.embedMedia,
      post,
    });

  return withWrapper ? <div className={wrapperClassName}>{cover}</div> : cover;
};

export default flowRight(withDeviceType, withSectionUrl)(PostListItemCover);
